import React from 'react';
import { motion } from 'framer-motion';

interface RecommendedScenarioCardProps {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  icon: string;
  difficulty?: string;
  isAvailable: boolean;
  matchReasons: string[];
  onSelect: () => void;
}

const getDifficultyColor = (difficulty: string | undefined): string => {
  if (!difficulty) return 'text-slate-400';
  switch (difficulty.toLowerCase()) {
    case 'beginner': return 'text-emerald-400';
    case 'intermediate': return 'text-yellow-400';
    case 'advanced': return 'text-orange-400';
    case 'expert': return 'text-red-400';
    case 'tech': return 'text-blue-400';
    case 'exec': return 'text-purple-400';
    default: return 'text-slate-400';
  }
};

export const RecommendedScenarioCard: React.FC<RecommendedScenarioCardProps> = ({
  title,
  subtitle,
  description,
  icon,
  difficulty,
  isAvailable,
  matchReasons,
  onSelect,
}) => {
  return (
    <motion.div
      className={`
        relative bg-white/90 dark:bg-gradient-to-br dark:from-dark-secondary dark:to-dark-accent
        border-2 border-aurora-violet-500/40 dark:border-aurora-violet-500/50
        rounded-2xl p-6 sm:p-8 backdrop-blur-md
        shadow-xl dark:shadow-aurora-card
        transition-all duration-300
        ${isAvailable
          ? 'cursor-pointer hover:scale-[1.02] hover:-translate-y-1 hover:shadow-2xl hover:border-aurora-violet-500/60'
          : 'cursor-not-allowed opacity-75'
        }
      `}
      onClick={isAvailable ? onSelect : undefined}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={isAvailable ? { scale: 1.02, y: -4 } : {}}
    >
      {/* Recommended Badge */}
      <div className="absolute -top-3 left-6">
        <motion.div
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
            bg-gradient-to-r from-aurora-violet-600 to-aurora-magenta-500
            text-white text-xs font-semibold uppercase tracking-wider
            shadow-lg shadow-aurora-violet-500/30"
          initial={{ scale: 0, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 500, damping: 30 }}
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          Recommended
        </motion.div>
      </div>

      {/* Icon and Status Row */}
      <div className="flex items-start justify-between mb-4 mt-2">
        <div className="text-4xl sm:text-5xl">{icon || '🎯'}</div>
        <div className="flex flex-col items-end gap-2">
          {!isAvailable && (
            <span className="bg-slate-200 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider">
              Coming Soon
            </span>
          )}
          {difficulty && (
            <span className={`text-sm font-medium ${getDifficultyColor(difficulty)}`}>
              {difficulty}
            </span>
          )}
        </div>
      </div>

      {/* Title and Subtitle */}
      <div className="mb-4">
        <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white mb-2">
          {title || 'Untitled Scenario'}
        </h2>
        {subtitle && (
          <h3 className="text-base sm:text-lg text-professional-blue-600 dark:text-aurora-cyan-400 font-medium">
            {subtitle}
          </h3>
        )}
      </div>

      {/* Description */}
      <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
        {description || 'No description available'}
      </p>

      {/* Match Reasons */}
      {matchReasons.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {matchReasons.map((reason, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                bg-aurora-violet-500/10 dark:bg-aurora-violet-500/20
                text-aurora-violet-700 dark:text-aurora-violet-300
                text-xs font-medium"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {reason}
            </span>
          ))}
        </div>
      )}

      {/* CTA Button */}
      <div className="flex justify-end">
        {isAvailable ? (
          <button className="bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 dark:from-aurora-violet-600 dark:to-aurora-violet-700 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg dark:hover:shadow-aurora-violet transition-all duration-200 hover:-translate-y-0.5">
            Start Simulation
          </button>
        ) : (
          <button disabled className="bg-slate-300 text-slate-500 dark:bg-slate-600/50 dark:text-slate-400 px-6 py-2 rounded-lg font-medium cursor-not-allowed">
            Coming Soon
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default RecommendedScenarioCard;
